import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import type { SelectChangeEvent } from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { SensorWiFiConfOptionsAnalysisTypeEnum, Threshold, VarName } from '../../../services/api';
import { varNameDetails } from '../../../utils/varNames';
import useStyles from '../../../styles/alertForm';
import { defaultHoursLimit, getTooltipSlotProps, selectOutlineSx } from '../helpers';
import { AnalysisOptions, HistoryAnalysisOptions } from './AnalysisOptionsContainer';

export enum ThresholdMode {
  minValue = 'minValue',
  maxValue = 'maxValue',
  minRange = 'minRange',
  maxRange = 'maxRange',
}

export const THRESHOLD_MODES = [
  {
    value: ThresholdMode.minValue,
    label: 'Below',
    description: 'Sends alert when value is below the input value',
  },
  {
    value: ThresholdMode.maxValue,
    label: 'Above',
    description: 'Sends alert when value is above the input value',
  },
  {
    value: ThresholdMode.minRange,
    label: 'Below Range',
    description: 'Sends alert when value is below the configured range',
  },
  {
    value: ThresholdMode.maxRange,
    label: 'Above Range',
    description: 'Sends alert when value is above the configured range',
  },
];

interface ThresholdProps {
  isHistoryType: boolean;
  value: AnalysisOptions;
  handleChange: (key: string, val: unknown) => void;
  allowEditing: boolean;
  varName: VarName;
}

interface ThresholdRow {
  id: string;
  mode: ThresholdMode;
  val: number | undefined;
}

let rowIdCounter = 0;
const nextRowId = () => `row-${rowIdCounter++}`;

const buildRowsFromThreshold = (threshold: Threshold | undefined): ThresholdRow[] => {
  const initial: ThresholdRow[] = [];

  if (threshold === undefined) {
    return initial;
  }

  THRESHOLD_MODES.forEach((m) => {
    const v = threshold[m.value];

    if (v !== undefined) {
      initial.push({ id: nextRowId(), mode: m.value, val: v });
    }
  });

  return initial;
};

function ThresholdSection({
  isHistoryType,
  value,
  handleChange,
  allowEditing,
  varName,
}: ThresholdProps) {
  const theme = useTheme();
  const { classes } = useStyles();
  const isWifiConf = value.analysisType === SensorWiFiConfOptionsAnalysisTypeEnum.SensorWiFiConf;

  const threshold = !isWifiConf ? (value.threshold as Threshold) : undefined;
  const hValue = value as HistoryAnalysisOptions;
  const { hoursLimit } = hValue;

  const totalHours = (hValue?.analysisPeriodDays ?? 1) * (hValue?.hoursOfDayFilter?.length || 24);
  const availableModes = useMemo(
    () =>
      THRESHOLD_MODES.filter(
        (m) =>
          isHistoryType ||
          (m.value !== ThresholdMode.minRange && m.value !== ThresholdMode.maxRange)
      ),
    [isHistoryType]
  );

  // rows as assigned threshold modes with value
  const [rows, setRows] = useState<ThresholdRow[]>(() => buildRowsFromThreshold(threshold));

  // Keep `rows` in sync if `value.threshold` changes from outside this
  // component (e.g. switching between saved configs, a reset action),
  // rather than only reading it once at mount.
  const lastThresholdRef = useRef(threshold);
  useEffect(() => {
    if (lastThresholdRef.current !== threshold) {
      lastThresholdRef.current = threshold;
      setRows(buildRowsFromThreshold(threshold));
    }
  }, [threshold]);

  // assign defaultHoursLimit if missing in analysis options for history type
  useEffect(() => {
    if (isHistoryType && !hoursLimit) {
      handleChange('hoursLimit', defaultHoursLimit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoryType]);

  // update new threshold in analysisOptions
  const emitChange = (updatedRows: ThresholdRow[]) => {
    const newThreshold = updatedRows.reduce<Threshold>((acc, row) => {
      if (row.val !== undefined) {
        acc[row.mode] = row.val;
      }
      return acc;
    }, {} as Threshold);
    handleChange('threshold', newThreshold);
  };

  const updateRows = (updater: (rows: ThresholdRow[]) => ThresholdRow[]) => {
    setRows((prev) => {
      const updated = updater(prev);
      emitChange(updated);
      return updated;
    });
  };

  const handleModeChange = (rowId: string, newMode: ThresholdMode) =>
    updateRows((prevRows) =>
      prevRows.map((row) => (row.id === rowId ? { ...row, mode: newMode } : row))
    );

  const handleValueChange = (rowId: string, newVal: number | undefined) =>
    updateRows((prevRows) =>
      prevRows.map((row) => (row.id === rowId ? { ...row, val: newVal } : row))
    );

  const handleDeleteRow = (rowId: string) =>
    updateRows((prevRows) => prevRows.filter((row) => row.id !== rowId));

  const handleAddRow = () =>
    updateRows((prevRows) => {
      const usedModes = new Set(prevRows.map((r) => r.mode));
      const unusedMode = availableModes.find((m) => !usedModes.has(m.value));
      return [
        ...prevRows,
        { id: nextRowId(), mode: unusedMode?.value ?? ThresholdMode.maxValue, val: undefined },
      ];
    });

  return (
    <Box className={classes.formContainer}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12 }}>
          <Tooltip
            title={
              <>
                <span>Input marginal value for the alert</span>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {availableModes.map((mode) => (
                    <li key={mode.value}>
                      <b>{mode.label}</b>: {mode.description}
                    </li>
                  ))}
                </ul>
              </>
            }
            placement="top"
            arrow
          >
            <InputLabel
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                width: 'fit-content',
              }}
            >
              and select threshold <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} /> &nbsp;
              <Box sx={{ whiteSpace: 'nowrap' }}>where {varNameDetails[varName].label} is </Box>
            </InputLabel>
          </Tooltip>
          <Stack spacing={1.5}>
            {rows.map((row, index) => (
              <Stack
                key={row.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap' }}
              >
                <Select
                  size="small"
                  value={row.mode}
                  onChange={(e: SelectChangeEvent) =>
                    handleModeChange(row.id, e.target.value as ThresholdMode)
                  }
                  disabled={!allowEditing}
                  sx={{
                    minWidth: 140,
                    ...selectOutlineSx(theme),
                  }}
                >
                  {/* disable if already selected previously */}
                  {availableModes.map((m) => {
                    const isSelectedByOtherRow = rows.some(
                      (r) => r.id !== row.id && r.mode === m.value
                    );

                    return (
                      <MenuItem key={m.value} value={m.value} disabled={isSelectedByOtherRow}>
                        {m.label}
                      </MenuItem>
                    );
                  })}
                </Select>

                <TextField
                  size="small"
                  type="number"
                  placeholder="e.g. 0"
                  value={row.val ?? ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    handleValueChange(row.id, newValue === '' ? undefined : Number(newValue));
                  }}
                  variant="outlined"
                  sx={{
                    width: 140,
                    ...selectOutlineSx(theme),
                  }}
                  disabled={!allowEditing}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography>{varNameDetails[varName].metric ?? ''}</Typography>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <IconButton
                  aria-label="delete threshold"
                  size="small"
                  onClick={() => handleDeleteRow(row.id)}
                  disabled={!allowEditing}
                >
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </IconButton>

                {index + 1 === rows.length && (
                  <IconButton
                    aria-label="Add threshold"
                    size="small"
                    onClick={handleAddRow}
                    disabled={!allowEditing || rows.length >= availableModes.length}
                  >
                    <AddIcon fontSize="small" color="success" />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>
          {isHistoryType && (
            <>
              <Grid size={{ xs: 12, sm: 12 }} sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
                <Box>for more than</Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Hours Limit"
                  type="number"
                  value={hoursLimit}
                  onChange={(e) =>
                    handleChange(
                      'hoursLimit',
                      e.target.value === '' ? undefined : Number(e.target.value)
                    )
                  }
                  placeholder="e.g. 4"
                  sx={{
                    ...selectOutlineSx(theme),
                    ml: 1,
                    width: '150px',
                  }}
                  slotProps={getTooltipSlotProps(
                    `Number of hours exceeding threshold before an alert (Must be greater than 0 and less than ${totalHours})`
                  )}
                  disabled={!allowEditing}
                  error={
                    Boolean(hoursLimit && hoursLimit >= totalHours) ||
                    hoursLimit === undefined ||
                    hoursLimit === 0
                  }
                />
                <Box sx={{ ml: 2 }}>
                  hours out of <b>{totalHours} hours</b>
                </Box>
              </Grid>
              {/* implement after the discussion */}
              {/* <Grid size={{ xs: 12 }} sx={{ ml: 1, mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={hValue.alertIfNotMatched ?? false}
                      onChange={(e) => handleChange('alertIfNotMatched', e.target.checked)}
                      color="success"
                      disabled={!allowEditing}
                    />
                  }
                  label="Alert if not matched (invert threshold)"
                />
              </Grid> */}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default ThresholdSection;
